import type { StyleRow, CustomFieldRowEntry } from './stylesFixtureData';
import { seasons, groups, brandOptions, suppliers, contactPersons } from './stylesFixtureData';
import { styleCustomFieldDefinitions } from './stylesCustomFieldDefinitions';

/**
 * Structural equality for audit-diffing bulk-edit fields, which — unlike the single-row PUT's
 * scalar `AUDITABLE_FIELDS` — are frequently nested objects (`season`, `group`, `brand`, …). A
 * bare `===` would always report "changed" for those since the new object is never the same
 * reference as the old one; `JSON.stringify` gives a stable-enough structural compare for these
 * plain-data rows (no functions/dates-as-objects in play).
 */
export const valuesEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a === 'object' && typeof b === 'object') {
    // Lookup-type bulk-edit fields (season/group/brand/supplier/…) round-trip as a denormalized
    // display object ({ id, name, … }) — the wire payload's version (built by config-cols.tsx's
    // `fieldTransforms`) only ever carries a subset of the stored row's keys (e.g. `season`
    // stores `customId`/`project` too, but the payload's `season` is just `{ id, name }`). A raw
    // structural `JSON.stringify` compare would call every such field "changed" even when its
    // `id` — the field's real identity — didn't move, which would defeat the "no-op logs
    // nothing" contract this diff exists to enforce. Compare by `id` when both sides have one.
    const objA = a as Record<string, unknown>;
    const objB = b as Record<string, unknown>;
    if (!Array.isArray(a) && !Array.isArray(b) && 'id' in objA && 'id' in objB) {
      return objA.id === objB.id;
    }
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  return false;
};

/**
 * Numeric `styleCustomFieldDefinitions[].type` → the string `CustomFieldRowEntry.type` a
 * persisted style row stores (mirrors `typeNumberToString` in `features/styles/types/Style.ts`,
 * which this mock cannot import — it's client-side FE code).
 */
const CF_TYPE_NUMBER_TO_STRING: Record<number, CustomFieldRowEntry['type']> = {
  0: 'allowedvalue',
  1: 'date',
  2: 'text',
  3: 'nestedallowedvalue',
  4: 'numeric',
  5: 'divider',
};
const CF_DEFINITION_TYPE_BY_ID = new Map(
  styleCustomFieldDefinitions.map(def => [def.id, def.type])
);

/**
 * Merges a bulk-edit request's `customFields` entries into a style's existing array by
 * `customFieldId`, replacing only the touched fields and keeping every other CF untouched.
 *
 * The wire payload (`transformInlineCustomFieldsToApi`) sends only `{ id, value }` — never a
 * `type` — so a naive `{...current, customFields: incoming}` overwrite would both wipe every
 * other CF on the row AND persist entries missing `type`, which `transformApiCustomField`'s
 * client-side read defaults to `'text'` (see `typeStringToNumber`'s fallback), silently
 * misrendering a Date (or any non-text) field on the next refetch. Resolving `type` from
 * `styleCustomFieldDefinitions` here mirrors what a real backend does server-side (it knows the
 * field's type from its own definition, never trusts the client to send it).
 */
export const mergeCustomFieldsForBulkUpdate = (
  current: CustomFieldRowEntry[] | undefined,
  incoming: unknown
): CustomFieldRowEntry[] => {
  if (!Array.isArray(incoming)) return current ?? [];
  const rows = incoming as Array<{
    id: number;
    value?: { id: number | null; value: string | null } | null;
  }>;
  const touchedIds = new Set(rows.map(cf => cf.id));
  const kept = (current ?? []).filter(cf => !touchedIds.has(cf.id));
  const merged: CustomFieldRowEntry[] = rows.map(cf => ({
    id: cf.id,
    type: CF_TYPE_NUMBER_TO_STRING[CF_DEFINITION_TYPE_BY_ID.get(cf.id) ?? 2] ?? 'text',
    value: cf.value ? { id: cf.value.id ?? 0, value: cf.value.value } : null,
  }));
  return [...kept, ...merged];
};

/**
 * Large-batch bulk-edit fixtures (DL-62594 epic — "Bulk edit styles: update execution,
 * feedback & performance", Testmo 1577259/1577264/1577267/1577272). The generic `generated`
 * rows in `styles.ts` cycle through all 6 `STYLE_STATES`, so only ~1/6 of any given slice is
 * editable — nowhere near the "~100 styles" the large-batch performance cases need in one
 * selection. These two blocks are real, persisted `stylesData` rows (not an ephemeral
 * `apiTransform` override) specifically so a bulk PUT against them round-trips through the same
 * find-by-id/merge/persist path as every other row, and the styles table's refetch after
 * "Apply changes" genuinely reflects the new values — an `apiTransform`-only override would
 * freeze the response at a fixed snapshot and could never show a post-update refetch.
 * Both sets are searchable by a unique name substring absent from every other fixture.
 *
 * Split out of `styles.ts` (DL-62594) purely to keep that file under the repo's 800-line cap —
 * these builders are otherwise only ever called from `styles.ts`'s `buildDefaultStylesData`.
 */
const BULK_PERF_FIXTURE_COUNT = 100;
export const buildBulkPerformanceFixtureStyles = (): StyleRow[] =>
  Array.from({ length: BULK_PERF_FIXTURE_COUNT }, (_, i) => {
    const n = i + 1;
    const id = 93000 + n;
    return {
      id,
      name: `BulkPerfFixture Style ${n}`,
      // "ST-9" prefix (not a distinct letter prefix) deliberately keeps this fixture sorting
      // AFTER every generated ST-1xxx row under an ascending "Style number" sort — the sibling
      // `warnings-and-selection-limits.spec.ts` (epic dl-62592) depends on id N landing at sorted
      // row index N-1 for the first 150 rows; a customId that sorts earlier (e.g. a bare "BP-"
      // prefix, which is lexically before "ST-") would shift that spec's row positions.
      customId: `ST-${id}`,
      description: '',
      state: 'unpublished', // every row editable — this is the point of the fixture
      logo: '',
      // seasons[2] (SS25) — the large-batch spec's tests all bulk-set Season to "AW26"
      // (`TARGET_SEASON_NAME` there), so seeding SS25 here keeps that a genuine change for
      // every row. Not seasons[0] (AW25): the bulk-edit Season combo's rendered listbox does
      // not actually offer "AW25" as an option (see that spec's `pickSeasonOption` for detail).
      season: seasons[2],
      group: groups[0],
      brand: {
        id: brandOptions[0].id,
        customId: brandOptions[0].userDefinedId ?? '',
        name: brandOptions[0].name,
        contactPersonId: contactPersons[0].id,
        contactPersonName: contactPersons[0].name,
      },
      supplier: {
        id: suppliers[0].id,
        customId: suppliers[0].customId,
        name: suppliers[0].name,
        contactPersonId: contactPersons[1].id,
        contactPersonName: contactPersons[1].name,
      },
      categories: [],
      styleColors: [],
      customFields: [],
      properties: [],
      lastUpdated: '2026-01-01T00:00:00',
      readyForExport: false,
    };
  });

/**
 * A smaller, deliberately mixed-state batch (25 editable + 5 non-editable) for the large-batch
 * "exclusion at scale" adversarial case — proves the Edit-click narrowing (`filterEditableItems`,
 * see the sibling `warnings-and-selection-limits.spec.ts` header) still works correctly when the
 * selection is large, not just the 2–3 row case already covered by that epic.
 */
const BULK_PERF_MIXED_EDITABLE_COUNT = 25;
const BULK_PERF_MIXED_NON_EDITABLE_STATES = [
  'delivered',
  'cancelled',
  'deleted',
  'delivered',
  'cancelled',
];
export const buildBulkPerformanceMixedFixtureStyles = (): StyleRow[] => {
  const editable = Array.from({ length: BULK_PERF_MIXED_EDITABLE_COUNT }, (_, i) => {
    const n = i + 1;
    const id = 93100 + n;
    return {
      id,
      name: `BulkPerfMixed Style ${n}`,
      customId: `ST-${id}`, // see the ST-9 prefix note on the BulkPerfFixture builder above
      description: '',
      state: 'unpublished',
      logo: '',
      season: seasons[2], // SS25 — see the seasons[2] note on the BulkPerfFixture builder above
      group: groups[0],
      brand: {
        id: brandOptions[0].id,
        customId: brandOptions[0].userDefinedId ?? '',
        name: brandOptions[0].name,
        contactPersonId: contactPersons[0].id,
        contactPersonName: contactPersons[0].name,
      },
      supplier: {
        id: suppliers[0].id,
        customId: suppliers[0].customId,
        name: suppliers[0].name,
        contactPersonId: contactPersons[1].id,
        contactPersonName: contactPersons[1].name,
      },
      categories: [],
      styleColors: [],
      customFields: [],
      properties: [],
      lastUpdated: '2026-01-01T00:00:00',
      readyForExport: false,
    };
  });
  const nonEditable = BULK_PERF_MIXED_NON_EDITABLE_STATES.map((state, i) => {
    const n = BULK_PERF_MIXED_EDITABLE_COUNT + i + 1;
    const id = 93100 + n;
    return {
      ...editable[0],
      id,
      name: `BulkPerfMixed Style ${n}`,
      customId: `ST-${id}`,
      state,
    };
  });
  return [...editable, ...nonEditable];
};

/**
 * Dedicated, individually-named pairs for the "update execution & feedback" normal-size bulk-edit
 * suite (`update-execution-and-feedback.spec.ts`, DL-62594 epic). Each pair shares a unique name
 * substring (absent everywhere else) so the suite can select exactly those two rows via the
 * table's own search box rather than an index into a virtualised row list — `rowCheckboxes.nth(N)`
 * only resolves when row N is actually mounted, which a `@tanstack/react-virtual`-backed table
 * does not guarantee for an arbitrary N without first scrolling it into view. All rows are in an
 * editable state (`unpublished`).
 *
 * The "Noop" pair's seasons are chosen so the spec's adversarial test can bulk-set Season to
 * `TARGET_SEASON_NAME` ("AW26" there — the bulk-edit Season combo's rendered listbox does not
 * actually offer "AW25" as an option, despite it being first in the mock's `seasons` array, so
 * every test targets AW26 instead; see that spec's `pickSeasonOption`): B starts on AW26 already
 * (so bulk-setting to AW26 is a genuine no-op for it), A starts on AW25 (so the same bulk-set is a
 * genuine change for it) — the deliberate mix the "no-op detection" test needs. The "Happy"/
 * "Failure" pairs both start away from AW26 (SS25/SS26) so a bulk-set to it is unambiguously real.
 */
export const buildBulkFeedbackFixtureStyles = (): StyleRow[] => {
  const base = (id: number, name: string, season: (typeof seasons)[number]): StyleRow => ({
    id,
    name,
    customId: `ST-${id}`,
    description: '',
    state: 'unpublished',
    logo: '',
    season,
    group: groups[0],
    brand: {
      id: brandOptions[0].id,
      customId: brandOptions[0].userDefinedId ?? '',
      name: brandOptions[0].name,
      contactPersonId: contactPersons[0].id,
      contactPersonName: contactPersons[0].name,
    },
    supplier: {
      id: suppliers[0].id,
      customId: suppliers[0].customId,
      name: suppliers[0].name,
      contactPersonId: contactPersons[1].id,
      contactPersonName: contactPersons[1].name,
    },
    categories: [],
    styleColors: [],
    customFields: [],
    properties: [],
    lastUpdated: '2026-01-01T00:00:00',
    readyForExport: false,
  });

  return [
    base(94001, 'BulkFeedbackFixture Happy A', seasons[2]), // SS25
    base(94002, 'BulkFeedbackFixture Happy B', seasons[3]), // SS26
    base(94003, 'BulkFeedbackFixture Noop A', seasons[0]), // AW25 — a genuine change to AW26
    base(94004, 'BulkFeedbackFixture Noop B', seasons[1]), // AW26 — already TARGET_SEASON_NAME
    base(94005, 'BulkFeedbackFixture Failure A', seasons[2]), // SS25
    base(94006, 'BulkFeedbackFixture Failure B', seasons[3]), // SS26
  ];
};

/**
 * Testmo 1577264's own manual case selects exactly **50** styles (not the 100 the sibling
 * happy-path test uses for 1577259/1577267) — a dedicated 50-row block so that story has a
 * scenario matching its literal case parameter, rather than being asserted only as a side effect
 * of the 100-row test. All rows editable (`unpublished`), seeded on SS25 so bulk-setting to
 * `TARGET_SEASON_NAME` ("AW26") is a genuine change for every row (see `buildBulkPerformanceFixtureStyles`'s
 * note on why SS25, not AW25, is the seed season).
 */
const BULK_TIMEOUT_FIXTURE_COUNT = 50;
export const buildBulkTimeoutFixtureStyles = (): StyleRow[] =>
  Array.from({ length: BULK_TIMEOUT_FIXTURE_COUNT }, (_, i) => {
    const n = i + 1;
    const id = 93200 + n;
    return {
      id,
      name: `BulkTimeoutFixture Style ${n}`,
      customId: `ST-${id}`, // see the ST-9 prefix note on the BulkPerfFixture builder above
      description: '',
      state: 'unpublished',
      logo: '',
      season: seasons[2], // SS25
      group: groups[0],
      brand: {
        id: brandOptions[0].id,
        customId: brandOptions[0].userDefinedId ?? '',
        name: brandOptions[0].name,
        contactPersonId: contactPersons[0].id,
        contactPersonName: contactPersons[0].name,
      },
      supplier: {
        id: suppliers[0].id,
        customId: suppliers[0].customId,
        name: suppliers[0].name,
        contactPersonId: contactPersons[1].id,
        contactPersonName: contactPersons[1].name,
      },
      categories: [],
      styleColors: [],
      customFields: [],
      properties: [],
      lastUpdated: '2026-01-01T00:00:00',
      readyForExport: false,
    };
  });

/**
 * Testmo 1577264's own manual case bulk-edits a **Date custom field** ("change a Date CF"), not
 * Season — its own description calls out "processing a large number of styles with **complex
 * changes**", which a single lookup-field change like Season doesn't exercise. A dedicated
 * 50-row block, distinct from `buildBulkTimeoutFixtureStyles` above (which covers 1577267's
 * literal "bulk edit Season" case at the same row count), so the two 50-row scenarios never
 * select the same rows and race when both specs' tests run in parallel. Every row starts on the
 * SAME "Delivery Date" (CF id 702) value — `2026-01-01` — so bulk-setting a different date is a
 * genuine change for all 50, mirroring `CustomFieldRowEntry`'s exact shape already used
 * elsewhere in `styles.ts` (`{ id, type: 'date', value: { id, value } }`).
 */
const BULK_TIMEOUT_DATE_FIXTURE_COUNT = 50;
export const buildBulkTimeoutDateFixtureStyles = (): StyleRow[] =>
  Array.from({ length: BULK_TIMEOUT_DATE_FIXTURE_COUNT }, (_, i) => {
    const n = i + 1;
    const id = 93300 + n;
    return {
      id,
      name: `BulkDateFixture Style ${n}`,
      customId: `ST-${id}`, // see the ST-9 prefix note on the BulkPerfFixture builder above
      description: '',
      state: 'unpublished',
      logo: '',
      season: seasons[2], // SS25 — unrelated to this fixture's Date-CF scenario, just a valid default
      group: groups[0],
      brand: {
        id: brandOptions[0].id,
        customId: brandOptions[0].userDefinedId ?? '',
        name: brandOptions[0].name,
        contactPersonId: contactPersons[0].id,
        contactPersonName: contactPersons[0].name,
      },
      supplier: {
        id: suppliers[0].id,
        customId: suppliers[0].customId,
        name: suppliers[0].name,
        contactPersonId: contactPersons[1].id,
        contactPersonName: contactPersons[1].name,
      },
      categories: [],
      styleColors: [],
      customFields: [{ id: 702, type: 'date', value: { id: 0, value: '2026-01-01' } }],
      properties: [],
      lastUpdated: '2026-01-01T00:00:00',
      readyForExport: false,
    };
  });
